import { useState, useCallback } from 'react';
import JSZip from 'jszip';
import { getProxyImageUrl } from '../utils';

/**
 * useExport - Custom hook for deck export functionality
 * Handles TTS export and image generation
 */
export function useExport(deckCards = []) {
  const [exportImageLoading, setExportImageLoading] = useState(false);
  const [showExportImageOptions, setShowExportImageOptions] = useState(false);
  const [exportImageOptions, setExportImageOptions] = useState({
    cardsPerRow: 5,
    numberOfRows: 6,
  });

  // Export deck as TTS-compatible JSON + images zip
  const exportToTTS = useCallback(async (deckName = 'Ultraman Deck') => {
    if (deckCards.length === 0) {
      throw new Error('No cards to export');
    }

    const totalCards = deckCards.reduce((sum, c) => sum + (parseInt(c.count) || 1), 0);
    if (totalCards !== 50) {
      throw new Error(`Deck must have exactly 50 cards (current: ${totalCards})`);
    }

    const safeDeckName = deckName.trim().replace(/[^a-z0-9-_]+/gi, '_') || 'deck';
    const BACK_URL = "https://steamusercontent-a.akamaihd.net/ugc/16420027240527538/483413ACD3746E956A903173304A14D98BA1A95A/";

    const customDeck = {};
    const deckIDs = [];
    const containedObjects = [];
    let deckIdCounter = 1;

    // Image priority for TTS export:
    // 1. images.tts.full - Steam CDN URLs (language-neutral, synced to all language variants)
    // 2. images.blob.full - Azure blob URLs (language-specific, uses current locale)
    // 3. images.original.full - Original API URLs (fallback)
    deckCards.forEach(card => {
      const count = parseInt(card.count) || 1;
      // Prefer TTS (Steam CDN) images which are synced across all languages
      const faceUrl = card.images?.tts?.full 
        || card.images?.tts?.thumbnail 
        || card.images?.blob?.full 
        || card.images?.blob?.thumbnail 
        || card.images?.original?.full 
        || card.images?.original?.thumbnail;

      if (!faceUrl) {
        console.warn(`No image found for card: ${card.name}`);
        return;
      }

      // Scene cards: stored as portrait PNGs (both Steam CDN and blob).
      // TTS uses rotY:90 to display them landscape on the table.
      const featureValue = (typeof card.feature === 'string'
        ? card.feature
        : card.feature?.value || '').toLowerCase();
      const isScene = featureValue === 'scene';
      const cardRotY = isScene ? 90 : 180;

      const deckKey = deckIdCounter.toString();
      const deckEntry = {
        FaceURL: faceUrl,
        BackURL: BACK_URL,
        NumWidth: 1,
        NumHeight: 1,
        BackIsHidden: true,
        UniqueBack: false,
      };

      customDeck[deckKey] = deckEntry;

      for (let i = 0; i < count; i++) {
        const cardId = deckIdCounter * 100;
        deckIDs.push(cardId);
        containedObjects.push({
          Name: 'Card',
          Transform: {
            posX: 0, posY: 0, posZ: 0,
            rotX: 0, rotY: cardRotY, rotZ: 180,
            scaleX: 1, scaleY: 1, scaleZ: 1,
          },
          Nickname: card.name || 'Unknown',
          Description: card.number || '',
          CardID: cardId,
          CustomDeck: { [deckKey]: deckEntry },
        });
      }

      deckIdCounter++;
    });

    const ttsObject = {
      ObjectStates: [{
        Name: 'DeckCustom',
        Nickname: safeDeckName,
        Transform: {
          posX: 0, posY: 1, posZ: 0,
          rotX: 0, rotY: 180, rotZ: 180,
          scaleX: 1, scaleY: 1, scaleZ: 1,
        },
        DeckIDs: deckIDs,
        CustomDeck: customDeck,
        ContainedObjects: containedObjects,
      }],
    };

    // Create zip file
    const zip = new JSZip();
    zip.file(`${safeDeckName}.json`, JSON.stringify(ttsObject, null, 2));

    const blob = await zip.generateAsync({ type: 'blob' });
    
    // Trigger download
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safeDeckName}_TTS.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    return { success: true, filename: `${safeDeckName}_TTS.zip` };
  }, [deckCards]);

  // Generate deck image
  const exportDeckImage = useCallback(async () => {
    if (deckCards.length === 0) {
      throw new Error('No cards to export');
    }

    setExportImageLoading(true);
    
    try {
      const { cardsPerRow, numberOfRows } = exportImageOptions;
      
      // Sort cards for consistent display
      const sortedCards = [...deckCards].sort((a, b) => {
        const aNum = a.number || '';
        const bNum = b.number || '';
        return aNum.localeCompare(bNum);
      });

      // Create canvas
      const cardWidth = 200;
      const cardHeight = 280;
      const padding = 10;
      
      const canvasWidth = cardsPerRow * (cardWidth + padding) + padding;
      const canvasHeight = numberOfRows * (cardHeight + padding) + padding;
      
      const canvas = document.createElement('canvas');
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const ctx = canvas.getContext('2d');
      
      // Fill background
      ctx.fillStyle = '#1a1a2e';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      
      // Expand cards by count and limit to visible area
      const expandedCards = [];
      sortedCards.forEach(card => {
        const count = parseInt(card.count) || 1;
        for (let i = 0; i < count; i++) {
          expandedCards.push(card);
        }
      });
      
      const visibleCards = expandedCards.slice(0, cardsPerRow * numberOfRows);
      
      // Load and draw images
      const loadImage = (url) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = getProxyImageUrl(url);
        });
      };

      for (let i = 0; i < visibleCards.length; i++) {
        const card = visibleCards[i];
        const row = Math.floor(i / cardsPerRow);
        const col = i % cardsPerRow;
        
        const x = padding + col * (cardWidth + padding);
        const y = padding + row * (cardHeight + padding);
        
        const imageUrl = card.images?.blob?.full || card.images?.original?.full;
        if (imageUrl) {
          try {
            const img = await loadImage(imageUrl);
            ctx.drawImage(img, x, y, cardWidth, cardHeight);
          } catch (err) {
            // Draw placeholder for failed images
            ctx.fillStyle = '#333';
            ctx.fillRect(x, y, cardWidth, cardHeight);
            ctx.fillStyle = '#fff';
            ctx.font = '12px sans-serif';
            ctx.fillText(card.name || 'Unknown', x + 10, y + cardHeight / 2);
          }
        }
      }
      
      // Trigger download
      const dataUrl = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = 'deck_image.png';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      return { success: true };
    } finally {
      setExportImageLoading(false);
      setShowExportImageOptions(false);
    }
  }, [deckCards, exportImageOptions]);

  return {
    // Image export
    exportImageLoading,
    showExportImageOptions,
    setShowExportImageOptions,
    exportImageOptions,
    setExportImageOptions,
    exportDeckImage,
    
    // TTS export
    exportToTTS,
  };
}

export default useExport;
